/**
 * Generic Repository Pattern for PocketBase
 * Provides type-safe CRUD operations with user scoping and error handling
 */

import { pbClient } from './pbClient';
import { AnyRecord, CollectionKey } from '@/types/collections';
import { resolveCollectionName } from './collectionMapping';

export interface QueryOptions {
  sort?: string;
  filter?: string;
  expand?: string;
  skipTotal?: boolean;
}

export interface ListOptions extends QueryOptions {
  page?: number;
  perPage?: number;
}

export class Repository<T extends AnyRecord> {
  private readonly collectionKey: CollectionKey;
  private readonly collectionName: string;
  private userId: string | null = null;

  constructor(collectionKey: CollectionKey, userId?: string) {
    this.collectionKey = collectionKey;
    this.collectionName = resolveCollectionName(collectionKey);
    if (userId) {
      this.userId = userId;
    }
  }

  getCollectionName(): string {
    return this.collectionName;
  }

  getCollectionKey(): CollectionKey {
    return this.collectionKey;
  }

  /**
   * Set the current user ID for scoped queries
   * Most collections are user_scoped, so we always filter by user
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  private buildUserFilter(): string {
    if (!this.userId) {
      throw new Error(`Repository: userId not set for collection "${this.collectionName}"`);
    }
    return `user = "${this.userId}"`;
  }

  /**
   * Combine user filter with additional filters
   */
  private combineFilters(userFilter: string, additionalFilter?: string): string {
    if (!additionalFilter) return userFilter;
    return `${userFilter} && (${additionalFilter})`;
  }

  /**
   * List all records for the current user
   */
  async list(options: ListOptions = {}): Promise<{ records: T[]; total: number }> {
    const userFilter = this.buildUserFilter();
    const filter = this.combineFilters(userFilter, options.filter);

    const result = await pbClient.listRecords<T>(this.collectionName, {
      page: options.page || 1,
      perPage: options.perPage || 50,
      filter,
      sort: options.sort || '-created',
      skipTotal: options.skipTotal ?? false,
      expand: options.expand,
    });

    return {
      records: result.items as T[],
      total: result.totalItems,
    };
  }

  /**
   * Get a single record by ID (with user scoping)
   */
  async get(id: string): Promise<T> {
    this.buildUserFilter();
    const result = await pbClient.getRecord<T>(this.collectionName, id);

    // Verify ownership
    const record = result as unknown as Record<string, unknown>;
    const owner = record.user ?? record.user_id;
    if (owner != null && owner !== this.userId) {
      throw new Error(`Unauthorized: record does not belong to user`);
    }

    return result as T;
  }

  /**
   * Create a new record (automatically set user)
   */
  async create(data: Partial<T>): Promise<T> {
    this.buildUserFilter();

    const payload = {
      ...data,
      user: this.userId,
    };

    const result = await pbClient.createRecord<T>(
      this.collectionName,
      payload as Record<string, unknown>
    );
    return result as T;
  }

  /**
   * Update a record (with ownership verification)
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    // Verify ownership first
    await this.get(id);

    const result = await pbClient.updateRecord<T>(
      this.collectionName,
      id,
      data as Record<string, unknown>
    );
    return result as T;
  }

  /**
   * Delete a record (with ownership verification)
   */
  async delete(id: string): Promise<void> {
    // Verify ownership first
    await this.get(id);

    await pbClient.deleteRecord(this.collectionName, id);
  }

  /**
   * Batch create records
   */
  async createMany(items: Array<Partial<T>>): Promise<T[]> {
    const promises = items.map((item) => this.create(item));
    return Promise.all(promises);
  }

  /**
   * Batch update records
   */
  async updateMany(updates: Array<{ id: string; data: Partial<T> }>): Promise<T[]> {
    const promises = updates.map(({ id, data }) => this.update(id, data));
    return Promise.all(promises);
  }

  /**
   * Batch delete records
   */
  async deleteMany(ids: string[]): Promise<void> {
    const promises = ids.map((id) => this.delete(id));
    await Promise.all(promises);
  }

}

/**
 * Factory function to create a repository for a specific collection
 */
export function createRepository<T extends AnyRecord>(
  collectionKey: CollectionKey
): Repository<T> {
  return new Repository<T>(collectionKey);
}
